<template lang="pug">
#send-document-detail-tab(v-cloak)
  .box-body.register-body
    // send datetime
    FormLabel(key-name="送付日時" :value="_sendDatetime")
    // send type
    FormLabel(key-name="宛先種別" :value="_typeLabel")
    // select targets
    FormLabel(key-name="宛先" :value="_sendTargetLabel")
      template(#textOption)
        button.btn(@click="onTargetList") 宛先一覧
    // select unit
    FormLabel(key-name="文書の配信単位" :value="_unitLabel")
    // same file selecter
    .register-row(v-if="_unit == 0")
      .register-group
        .register-key
          span.key-name 添付文書
        .register-value
          .d-flex.flex-column
            ul.list-group.mb-2(v-for="fileName in _fileNames")
              li.list-group-item.file-name {{ fileName }}
            button.btn.preview-btn(@click="onTempFilePreview") プレビュー
    // separate file selecter
    .register-row(v-if="_unit == 1")
      .register-group
        .register-key
          span.key-name 添付文書
        .register-value
          button.btn.separate-file-select-btn(@click="onSeparateFileList") ファイル一覧
    // category
    FormLabel(key-name="文書種類" :value="_category")
    // title and text
    FormLabel(key-name="タイトル" :value="_title")
    FormLabel(key-name="本文" :value="_text")
    // option confirm buttons
    .register-row(v-if="_needAgreement")
      .register-group
        .register-key
          span.key-name オプション：同意取得
        .register-value
          .d-flex.flex-row.flex-wrap
            button.btn.mr-2(@click="onAgreementList(sendDocument)" type="button") 同意済一覧（{{ _replyCount }}人）
            button.btn.mr-2(@click="onNonAgreementList(sendDocument)" type="button") 未同意一覧（{{ _nonreplyCount }}人）
            div(v-if="_permissions.useControl")
              button.btn.disable(v-if="_disableRemind" type="button" disabled) 未同意者に再送
              button.btn(v-else @click="onRemind(sendDocument)" type="button") 未同意者に再送
    .register-row(v-if="_needConfirm")
      .register-group
        .register-key
          span.key-name オプション：確認取得
        .register-value
          .d-flex.flex-row.flex-wrap
            button.btn.mr-2(@click="onConfirmList(sendDocument)" type="button") 確認済一覧（{{ _replyCount }}人）
            button.btn.mr-2(@click="onNonConfirmList(sendDocument)" type="button") 未確認一覧（{{ _nonreplyCount }}人）
            div(v-if="_permissions.useControl")
              button.btn.disable(v-if="_disableRemind" type="button" disabled) 未確認者に再送
              button.btn(v-else @click="onRemind(sendDocument)" type="button") 未確認者に再送

  TargetFamilyDetailModal(v-model="showTargetFamilyListModal" :items="targetFamilyListItems" :title="_title")
  TargetOtherUserDetailModal(v-model="showTargetOtherUserListModal" :items="targetOtherUserListItems" :title="_title")

  SendMessageFilePreviewModal(ref="previewModal")
  SeparateFileListModal(v-model="showSeparateFileListModal"
    :items="separateFileTargetsItems" :rows.sync="separateFileTargetsRows" :isLoading="separateFileTargetsIsLoading")

  OptionTargetFamilyListModal(v-model="showOptionTargetFamilyListModal" :modalType="optionTargetFamilyListModalType"
    :items.sync="optionTargetFamilyItems" :sendDocument="sendDocument" :isLoading="isOptionTargetFamilyListModalLoading")
  OptionTargetOtherUserListModal(v-model="showOptionTargetOtherUserListModal" :modalType="optionTargetOtherUserListModalType"
    :items.sync="optionTargetOtherUserItems" :sendDocument="sendDocument" :isLoading="isOptionTargetOtherUserListModalLoading")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import Setting from "../../../../public/setting.json";
import SystemError from "@/commons/models/SystemError";

import SendDocumentsService, {
  Type,
  Unit,
  SendDocumentGetModel,
  SendDocumentReplyGetModel,
  SendDocumentFamilyReceiver,
  SendDocumentOtherUserReceiver,
  SendMessageGroupInfo,
  ResidentInfo,
  OtherUserGroupInfo,
  GroupOtherUserGroupInfo
} from "@/commons/services/SendDocuments";
import { PlanType } from "@/commons/services/Plans";
import S3UrlService from "@/commons/services/S3Url";
import SendDocumentCategoriesService, { SendDocumentCategoryGetModel } from "@/commons/services/SendDocumentCategories";
import ResidentsService, { ResidentGetModel, ResidentStatus } from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormMultiFile from "@/pc/components/forms/FormMultiFile.vue";
import DraggableFiles from "@/pc/components/DraggableFiles.vue";

import TargetFamilyDetailModal from "@/pc/containers/sendDocuments/TargetFamilyDetailModal.vue";
import { TargetFamilyDetailTableItem } from "@/pc/containers/sendDocuments/TargetFamilyDetailTable.vue";
import TargetOtherUserDetailModal from "@/pc/containers/sendDocuments/TargetOtherUserDetailModal.vue";
import { TargetOtherUserDetailTableItem } from "@/pc/containers/sendDocuments/TargetOtherUserDetailTable.vue";
import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";
import SeparateFileListModal, { SendTargetExtFiles } from "@/pc/containers/sendDocuments/SeparateFileListModal.vue";
import OptionTargetFamilyListModal, { OptionTargetFamilyListModalType } from "@/pc/containers/sendDocuments/OptionTargetFamilyListModal.vue";
import { OptionTargetFamilyTableItem } from "@/pc/containers/sendDocuments/OptionTargetFamilyTable.vue";
import OptionTargetOtherUserListModal, { OptionTargetOtherUserListModalType } from "@/pc/containers/sendDocuments/OptionTargetOtherUserListModal.vue";
import { OptionTargetOtherUserTableItem } from "@/pc/containers/sendDocuments/OptionTargetOtherUserTable.vue";
import { GroupType } from "@/commons/services/SendMessageGroups";
import { SendDocumentRemindModel } from "@/pc/pages/sendDocuments/SendDocumentDetail.vue";
import { SeparateFileTableItem } from "./SeparateFileTable.vue";
import { SendDocumentsFunction } from "@/commons/services/Authorization";
import { ResidentGroupInfo } from "@/commons/services/Questionnaires";

/**
 * ### 文書送付確認画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    FormLabel,
    FormMultiFile,
    DraggableFiles,
    TargetFamilyDetailModal,
    TargetOtherUserDetailModal,
    SendMessageFilePreviewModal,
    SeparateFileListModal,
    OptionTargetFamilyListModal,
    OptionTargetOtherUserListModal
  }
})
export default class SendDocumentDetailTab extends Vue {
  /** 文書送付サービス */
  @Inject("SendDocumentsService") sendDocumentsService!: SendDocumentsService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;
  /** 文書送付カテゴリーサービス */
  @Inject("SendDocumentCategoriesService") categoriesService!: SendDocumentCategoriesService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** ID */
  mainId: number | null = null;
  /** 文書送付 */
  sendDocument: SendDocumentGetModel | null = null;
  /** 利用者グループMap(key:利用者グループID value:利用者グループ) */
  residentGroupMap: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();
  /** 文書送付カテゴリMAP(key:id, value:name) */
  categoryMap: Map<number, string> = new Map<number, string>();

  /** 宛先全員同じ文章のファイル */
  sameFiles: Array<File> | null = null;
  /** 利用者ごとの異なる文書のファイルリストモーダル表示可否 */
  showSeparateFileListModal: boolean = false;
  /** 利用者ごとの異なる文書の宛先対象 */
  separateFileTargets: Array<SendTargetExtFiles> = [];
  /** 利用者ごとの異なる文書のファイル選択モーダルの宛先対象 */
  separateFileTargetsItems: Array<SeparateFileTableItem> = [];
  /** 利用者ごとの異なる文書の宛先行数 */
  separateFileTargetsRows: number = 0;
  /** 利用者ごとの異なる文書の宛先ローディング */
  separateFileTargetsIsLoading: boolean = false;
  /** 利用者ごとの異なる文書の選択ファイル */
  separateFiles: Array<any> | null = null;

  /** 宛先一覧モーダル(家族)表示可否 */
  showTargetFamilyListModal: boolean = false;
  /** 宛先一覧モーダル(家族) */
  targetFamilyListItems: Array<TargetFamilyDetailTableItem> = [];
  /** 宛先一覧モーダル(施設関係者)表示可否 */
  showTargetOtherUserListModal: boolean = false;
  /** 宛先一覧モーダル(施設関係者) */
  targetOtherUserListItems: Array<TargetOtherUserDetailTableItem> = [];

  /** 文書送付の返信(家族ユーザー) */
  familyReplyItems: Array<OptionTargetFamilyTableItem> = [];
  /** 文書送付の未読家族ユーザー */
  familyNonReplyItems: Array<OptionTargetFamilyTableItem> = [];
  /** 文書送付の返信(施設関係者ユーザー) */
  otherUserReplyItems: Array<OptionTargetOtherUserTableItem> = [];
  /** 文書送付の未読施設関係者メンバー */
  otherUserNonReplyItems: Array<OptionTargetOtherUserTableItem> = [];

  /** 文書送付の同意・未同意モーダル表示可否(家族ユーザー) */
  showOptionTargetFamilyListModal: boolean = false;
  /** 文書送付の同意・未同意モーダル種別 */
  optionTargetFamilyListModalType: OptionTargetFamilyListModalType = OptionTargetFamilyListModalType.UNKNOWN;
  /** 文書送付の同意・未同意モーダルローディング表示可否 */
  isOptionTargetFamilyListModalLoading: boolean = false;
  /** 文書送付の同意・未同意モーダル用の家族ユーザー */
  optionTargetFamilyItems: Array<OptionTargetFamilyTableItem> = [];

  /** 文書送付の同意・未同意モーダル表示可否(家族ユーザー) */
  showOptionTargetOtherUserListModal: boolean = false;
  /** 文書送付の同意・未同意モーダルタイトル */
  optionTargetOtherUserListModalType: OptionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.UNKNOWN;
  /** 文書送付の同意・未同意モーダルローディング表示可否 */
  isOptionTargetOtherUserListModalLoading: boolean = false;
  /** 文書送付の同意・未同意モーダル用の家族ユーザー */
  optionTargetOtherUserItems: Array<OptionTargetOtherUserTableItem> = [];

  /** ファイル登録数 */
  readonly registerFileUploadLimit: number = 9;

  get _permissions(): SendDocumentsFunction {
    return userStore.loginUser.permissions.sendDocumentsFunction;
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    this.planType = userStore.facility.planType;

    await this.createView();

    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 宛先を取得する
   * - 編集画面なので編集対象の文書送付を取得して入力要素に反映させる
   * - 既に文書送付が送信済みの場合、再度送信させてはいけないので404画面へ遷移させる
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.mainId = Number(this.$route.params.id);

    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const residentGroupMap: Map<number, ResidentGroupsGetModel> = this.createResidentGroupMap(residentGroups);
    this.residentGroupMap = residentGroupMap;

    const sendDocumentCategories: Array<SendDocumentCategoryGetModel> = await this.getSendDocumentCategories();
    const categoryMap: Map<number, string> = this.createCategoryMap(sendDocumentCategories);
    this.categoryMap = categoryMap;

    const sendDocument: SendDocumentGetModel = await this.getSendDocument(Number(this.$route.params.id));
    this.sendDocument = sendDocument;

    await this.setReplies(sendDocument);
    this.setReceiverDetails(sendDocument);

    this.isBusy = false;

    return;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループMAPを作成する
   *
   * - key：グループID
   * - value：グループ情報
   */
  public createResidentGroupMap(groups: Array<ResidentGroupsGetModel>): Map<number, ResidentGroupsGetModel> {
    const map: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();
    for (const group of groups) {
      map.set(group.id, group);
    }
    return map;
  }

  /**
   * ### 文書送付カテゴリーを取得する
   */
  public async getSendDocumentCategories(): Promise<Array<SendDocumentCategoryGetModel>> {
    const response: Array<SendDocumentCategoryGetModel> = await this.categoriesService.gets(userStore);
    return response;
  }

  /**
   * ### IDから名称変換用にカテゴリのMAPを作成する
   */
  public createCategoryMap(categories: Array<SendDocumentCategoryGetModel>): Map<number, string> {
    const map: Map<number, string> = new Map<number, string>();
    for (const category of categories) {
      map.set(category.id, category.name);
    }
    return map;
  }

  /**
   * ### 文書送付を取得する
   */
  public async getSendDocument(mainId: number): Promise<SendDocumentGetModel> {
    const response: Array<SendDocumentGetModel> = await this.sendDocumentsService.get(userStore, mainId);
    for (const sendDocument of response) {
      if (sendDocument.mainId == mainId) {
        return sendDocument;
      }
    }
    return response[0];
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  /**
   * ### 同意／未同意、確認／未確認のモーダル表示のために返信情報を格納する
   */
  public async setReplies(sendDocument: SendDocumentGetModel): Promise<void> {
    const replies: Array<SendDocumentReplyGetModel> = await this.getReplies(sendDocument.mainId);

    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        const itemsAndMap: { items: Array<OptionTargetFamilyTableItem>; map: Map<string, string> } = this.createFamilyReplyItemsAndMap(replies);
        this.familyReplyItems = itemsAndMap.items;

        const familyNonReplyItems: Array<OptionTargetFamilyTableItem> = this.createNonReplyFamilies(sendDocument, itemsAndMap.map);
        this.familyNonReplyItems = familyNonReplyItems;

        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        const itemsAndMap: { items: Array<OptionTargetOtherUserTableItem>; map: Map<string, string> } = this.createOtherUserReplyItemsAndMap(replies);
        this.otherUserReplyItems = itemsAndMap.items;

        const otherUserNonReplyItems: Array<OptionTargetOtherUserTableItem> = this.createNonReplyOtherUser(sendDocument, itemsAndMap.map);
        this.otherUserNonReplyItems = otherUserNonReplyItems;

        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          const itemsAndMap: { items: Array<OptionTargetFamilyTableItem>; map: Map<string, string> } = this.createFamilyReplyItemsAndMap(replies);
          this.familyReplyItems = itemsAndMap.items;

          const familyNonReplyItems: Array<OptionTargetFamilyTableItem> = this.createNonReplyFamilies(sendDocument, itemsAndMap.map);
          this.familyNonReplyItems = familyNonReplyItems;

          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          const itemsAndMap: { items: Array<OptionTargetOtherUserTableItem>; map: Map<string, string> } = this.createOtherUserReplyItemsAndMap(replies);
          this.otherUserReplyItems = itemsAndMap.items;

          const otherUserNonReplyItems: Array<OptionTargetOtherUserTableItem> = this.createNonReplyOtherUser(sendDocument, itemsAndMap.map);
          this.otherUserNonReplyItems = otherUserNonReplyItems;

          break;
        }
      }
    }

    return;
  }

  /**
   * ### 文書送付の返信を取得する
   */
  public async getReplies(mainId: number): Promise<Array<SendDocumentReplyGetModel>> {
    const response: Array<SendDocumentReplyGetModel> = await this.sendDocumentsService.getReplies(userStore, mainId);
    return response;
  }

  /**
   * ### 家族の返信、返信情報のMAPを作成する
   */
  public createFamilyReplyItemsAndMap(replies: Array<SendDocumentReplyGetModel>): { items: Array<OptionTargetFamilyTableItem>; map: Map<string, string> } {
    let items: Array<OptionTargetFamilyTableItem> = [];
    let map: Map<string, string> = new Map<string, string>();

    for (const reply of replies) {
      map.set(`${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`, `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`);

      const residentInfo: ResidentInfo = reply.residentInfo as ResidentInfo;
      items.push({
        residentsSortKey: `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`,
        groupId: residentInfo.groupId,
        groupName: residentInfo.groupName,
        id: residentInfo.id,
        name: residentInfo.name,
        roomId: residentInfo.roomId,
        room: residentInfo.room,
        familyId: 0,
        familyName: residentInfo.familyName,
        familyRelation: residentInfo.familyRelation,
        familyPosition: residentInfo.familyPosition,
        familyTel: residentInfo.familyTel,
        familyLineId: residentInfo.familyLineId,
        familyMailaddress: residentInfo.familyMailaddress,
        senderType: residentInfo.senderType,
        datetime: reply.datetime,
        message: reply.message,
        insuredNumber: residentInfo.insuredNumber ? residentInfo.insuredNumber : null,
        careInsuranceInsuredNumber: residentInfo.careInsuranceInsuredNumber ? residentInfo.careInsuranceInsuredNumber : null,
        source: reply.residentInfo,
        isTarget: true,
        key: `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`,
        displayName: residentInfo.familyName
      });
    }

    return { items, map };
  }

  /**
   * ### 家族の未返信を作成する
   */
  public createNonReplyFamilies(sendDocument: SendDocumentGetModel, map: Map<string, string>): Array<OptionTargetFamilyTableItem> {
    let items: Array<OptionTargetFamilyTableItem> = [];

    for (const receiver of sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
      const replyed: string | undefined = map.get(receiver.residentsSortKey);
      if (replyed != undefined) {
        continue;
      }

      let insuredNumber: string | null = null;
      let careInsuranceInsuredNumber: string | null = null;
      if (receiver.insuredNumberKey) {
        const insuredNumbers = receiver.insuredNumberKey.split("_");
        insuredNumber = insuredNumbers[0];
        careInsuranceInsuredNumber = insuredNumbers[1];
      }

      items.push({
        residentsSortKey: receiver.residentsSortKey,
        groupId: receiver.groupId,
        groupName: receiver.groupName,
        id: receiver.id,
        name: receiver.name,
        roomId: receiver.roomId,
        room: receiver.room,
        familyId: receiver.familyId,
        familyName: receiver.familyName,
        familyRelation: receiver.familyRelation,
        familyPosition: receiver.familyPosition as string | null,
        familyTel: receiver.familyTel,
        familyLineId: receiver.familyLineId,
        familyMailaddress: receiver.familyMailaddress,
        senderType: receiver.senderType,
        datetime: null,
        message: "",
        insuredNumber: insuredNumber,
        careInsuranceInsuredNumber: careInsuranceInsuredNumber,
        source: receiver,
        isTarget: true,
        key: receiver.residentsSortKey,
        displayName: receiver.familyName
      });
    }

    return items;
  }

  /**
   * ### 施設関係者の返信、返信情報のMAPを作成する
   */
  public createOtherUserReplyItemsAndMap(replies: Array<SendDocumentReplyGetModel>): { items: Array<OptionTargetOtherUserTableItem>; map: Map<string, string> } {
    let items: Array<OptionTargetOtherUserTableItem> = [];
    let map: Map<string, string> = new Map<string, string>();

    for (const reply of replies) {
      map.set(`${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`, `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`);

      const otherUserGroupInfo: OtherUserGroupInfo = reply.otherUserGroupInfo as OtherUserGroupInfo;
      items.push({
        otherUserGroupsSortKey: `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`,
        id: otherUserGroupInfo.id,
        name: otherUserGroupInfo.name,
        groupTypeId: otherUserGroupInfo.typeId,
        groupTypeName: otherUserGroupInfo.typeName,
        memberId: otherUserGroupInfo.memberId,
        memberName: otherUserGroupInfo.memberName,
        memberTel: otherUserGroupInfo.memberTel,
        memberOrganization: otherUserGroupInfo.memberOrganization,
        memberLineId: otherUserGroupInfo.memberLineId,
        memberMailaddress: otherUserGroupInfo.memberMailaddress,
        senderType: otherUserGroupInfo.senderType,
        message: reply.message,
        datetime: reply.datetime,
        source: reply.otherUserGroupInfo,
        isTarget: true,
        key: `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`,
        displayName: otherUserGroupInfo.memberName
      });
    }

    return { items, map };
  }

  /**
   * ### 施設関係者の未返信を作成する
   */
  public createNonReplyOtherUser(sendDocument: SendDocumentGetModel, map: Map<string, string>): Array<OptionTargetOtherUserTableItem> {
    let items: Array<OptionTargetOtherUserTableItem> = [];

    for (const receiver of sendDocument.receivers as Array<SendDocumentOtherUserReceiver>) {
      const replyed: string | undefined = map.get(receiver.otherUserGroupsSortKey);
      if (replyed != undefined) {
        continue;
      }

      items.push({
        otherUserGroupsSortKey: receiver.otherUserGroupsSortKey,
        id: receiver.id,
        name: receiver.name,
        groupTypeId: receiver.groupTypeId,
        groupTypeName: receiver.groupTypeName,
        memberId: receiver.memberId,
        memberName: receiver.memberName,
        memberTel: receiver.memberTel,
        memberOrganization: receiver.memberOrganization,
        memberLineId: receiver.memberLineId,
        memberMailaddress: receiver.memberMailaddress,
        senderType: receiver.senderType,
        datetime: null,
        message: "",
        source: receiver,
        isTarget: true,
        key: receiver.otherUserGroupsSortKey,
        displayName: receiver.memberName
      });
    }

    return items;
  }

  /**
   * ### 宛先詳細モーダルで表示する宛先情報を格納する
   */
  public setReceiverDetails(sendDocument: SendDocumentGetModel): void {
    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        let receiverDetails: Array<TargetFamilyDetailTableItem> = [];
        for (const receiver of sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
          receiverDetails.push({ ...receiver });
        }
        this.targetFamilyListItems = receiverDetails;
        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        let receiverDetails: Array<TargetOtherUserDetailTableItem> = [];
        for (const receiver of sendDocument.receivers as Array<SendDocumentOtherUserReceiver>) {
          receiverDetails.push({ ...receiver });
        }
        this.targetOtherUserListItems = receiverDetails;
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          let receiverDetails: Array<TargetFamilyDetailTableItem> = [];
          for (const receiver of sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
            receiverDetails.push({ ...receiver });
          }
          this.targetFamilyListItems = receiverDetails;
          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          let receiverDetails: Array<TargetOtherUserDetailTableItem> = [];
          for (const receiver of sendDocument.receivers as Array<SendDocumentOtherUserReceiver>) {
            receiverDetails.push({ ...receiver });
          }
          this.targetOtherUserListItems = receiverDetails;
          break;
        }
      }
    }

    return;
  }

  /**
   * ### 文書送付一覧に遷移する
   */
  public gotoSendDocumentList(): void {
    this.$router.push(PcUrl.SendDocumentList);
    return;
  }

  /**
   * ### 宛先一覧ボタン押下イベント
   */
  public onTargetList(): void {
    if (this.sendDocument == null) {
      return;
    }

    switch (this.sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY: {
        this.showTargetFamilyListModal = true;
        break;
      }
      case Type.SELECTED_OTHER_USER: {
        this.showTargetOtherUserListModal = true;
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (this.sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${this.sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (this.sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          this.showTargetFamilyListModal = true;
          break;
        } else if (this.sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          this.showTargetOtherUserListModal = true;
          break;
        }
        break;
      }
      case Type.RESIDENT_GROUP: {
        if (this.sendDocument.residentGroupInfos == null) {
          let systemError: SystemError = new SystemError(`residentGroupInfos is null. type:${this.sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }
        this.showTargetFamilyListModal = true;
        break;
      }
      case Type.OTHER_USER_GROUP: {
        if (this.sendDocument.otherUserGroupInfos == null) {
          let systemError: SystemError = new SystemError(`otherUserGroupInfos is null. type:${this.sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }
        this.showTargetOtherUserListModal = true;
        break;
      }
    }

    return;
  }

  /**
   * ### 添付ファイルプレビューボタン押下イベント処理
   *
   * - ダイアログで添付ファイルをプレビュー表示する
   */
  public async onTempFilePreview(): Promise<void> {
    // ありえないがnullの場合は何もしない
    if (!this.sendDocument) {
      return;
    }
    if (!this.sameFiles) {
      this.isBusy = true;
      this.loadingMessage = "読込み中...";

      let files: Array<File> = [];
      for (const sameFiles of this.sendDocument.fileUrls) {
        if (sameFiles.sortKey != "none") {
          continue;
        }
        for (const file of sameFiles.files) {
          const updatedFile: File = await this.getS3File(file.url, file.name);
          files.push(updatedFile);
        }
      }
      this.sameFiles = files;
      this.separateFiles = [];

      this.isBusy = false;
    }

    const modal: SendMessageFilePreviewModal | Vue | Element | Vue[] | Element[] = this.$refs.previewModal;
    if (modal instanceof SendMessageFilePreviewModal) {
      modal.show("", false, this.sameFiles);
    }
    return;
  }

  /**
   * ### 利用者ごとに異なる文書の一覧モーダルを表示する
   */
  public async onSeparateFileList(): Promise<void> {
    // ありえないがnullの場合は何もしない
    if (!this.sendDocument) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";
    this.separateFileTargetsIsLoading = true;

    if (!this.separateFiles) {
      const separateFiles: Array<ResidentSeparateFiles> = [];
      const separateFileTargets: Array<SendTargetExtFiles> = [];
      for (const separateFile of this.sendDocument.fileUrls) {
        separateFiles.push({
          sortKey: separateFile.sortKey,
          files: separateFile.files as any
        });
      }

      const sortKeyFilesMap: Map<string, Array<File>> = new Map<string, Array<File>>();
      for (const files of separateFiles) {
        sortKeyFilesMap.set(files.sortKey, files.files);
      }

      switch (this.sendDocument.type) {
        case Type.ALL_FAMILY:
        case Type.SELECTED_FAMILY:
        case Type.SEND_MESSAGE_GROUP:
        case Type.RESIDENT_GROUP: {
          for (const receiver of this.sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
            const files: Array<File> | undefined = sortKeyFilesMap.get(receiver.residentsSortKey as string);
            separateFileTargets.push({
              isTarget: true,
              key: receiver.residentsSortKey,
              insuredNumberKey: null,
              label: receiver.familyName,
              files: files != undefined ? files : [],
              source: receiver
            });
          }
          break;
        }
      }

      this.sameFiles = [];
      this.separateFiles = separateFiles;
      this.separateFileTargets = separateFileTargets;
    }

    let sortKeyFilesMap: Map<string, Array<File>> = new Map<string, Array<File>>();
    for (const files of this.separateFiles) {
      sortKeyFilesMap.set(files.sortKey, files.files);
    }
    // モーダル表示で使用する情報を作成する
    const separateFileTargetsItems: Array<SeparateFileTableItem> = [];
    const residents: Array<ResidentGetModel> = await this.getResidents();
    const items: Array<SeparateFileTableItem> = [];
    for (const resident of residents) {
      if (resident.familyCount == 0) {
        continue;
      }
      const group: ResidentGroupsGetModel | undefined = this.residentGroupMap.get(resident.groupId);
      items.push({
        residentSortKey: resident.residentsSortKey,
        groupId: resident.groupId,
        groupName: group ? group.name : "",
        residentId: resident.id,
        residentName: resident.name,
        room: resident.room,
        insuredNumberKey: `${resident.insuredNumber}_${resident.careInsuranceInsuredNumber}`,
        targetFamilies: [],
        files: []
      });
    }
    for (const item of items) {
      const targetFamilies: Array<SendTargetExtFiles> = [];
      for (const target of this.separateFileTargets) {
        if (item.residentId == target.source.id && target.isTarget == true) {
          targetFamilies.push({
            ...target,
            label: `${target.source.familyName}（${target.source.familyRelation}）`
          });
        }
      }
      if (0 < targetFamilies.length) {
        item.targetFamilies = targetFamilies;
        item.files = targetFamilies[0].files;
        separateFileTargetsItems.push(item);
      }
    }

    this.isBusy = false;
    this.separateFileTargetsItems = separateFileTargetsItems;
    this.separateFileTargetsRows = separateFileTargetsItems.length;
    this.separateFileTargetsIsLoading = false;
    this.showSeparateFileListModal = true;
    return;
  }

  /**
   * ### 保険番号を家族に付与するために利用者を取得する
   */
  public async getResidents(): Promise<Array<ResidentGetModel>> {
    const response: Array<ResidentGetModel> = await this.residentsService.gets(userStore, { addDeleted: "true" });
    return response;
  }

  /**
   * ### 同意済一覧ボタン押下イベント
   */
  public onAgreementList(sendDocument: SendDocumentGetModel): void {
    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        this.isOptionTargetFamilyListModalLoading = true;
        this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.AGREEMENT;
        this.optionTargetFamilyItems = this.familyReplyItems;
        this.isOptionTargetFamilyListModalLoading = false;
        this.showOptionTargetFamilyListModal = true;

        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        this.isOptionTargetOtherUserListModalLoading = true;
        this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.AGREEMENT;
        this.optionTargetOtherUserItems = this.otherUserReplyItems;
        this.isOptionTargetOtherUserListModalLoading = false;
        this.showOptionTargetOtherUserListModal = true;

        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          this.isOptionTargetFamilyListModalLoading = true;
          this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.AGREEMENT;
          this.optionTargetFamilyItems = this.familyReplyItems;
          this.isOptionTargetFamilyListModalLoading = false;
          this.showOptionTargetFamilyListModal = true;

          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          this.isOptionTargetOtherUserListModalLoading = true;
          this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.AGREEMENT;
          this.optionTargetOtherUserItems = this.otherUserReplyItems;
          this.isOptionTargetOtherUserListModalLoading = false;
          this.showOptionTargetOtherUserListModal = true;

          break;
        }
      }
    }

    return;
  }

  /**
   * ### 未同意一覧ボタン押下イベント
   */
  public onNonAgreementList(sendDocument: SendDocumentGetModel): void {
    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        this.isOptionTargetFamilyListModalLoading = true;
        this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.NON_AGREEMENT;
        this.optionTargetFamilyItems = this.familyNonReplyItems;
        this.isOptionTargetFamilyListModalLoading = false;
        this.showOptionTargetFamilyListModal = true;

        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        this.isOptionTargetOtherUserListModalLoading = true;
        this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.NON_AGREEMENT;
        this.optionTargetOtherUserItems = this.otherUserNonReplyItems;
        this.isOptionTargetOtherUserListModalLoading = false;
        this.showOptionTargetOtherUserListModal = true;

        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          this.isOptionTargetFamilyListModalLoading = true;
          this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.NON_AGREEMENT;
          this.optionTargetFamilyItems = this.familyNonReplyItems;
          this.isOptionTargetFamilyListModalLoading = false;
          this.showOptionTargetFamilyListModal = true;

          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          this.isOptionTargetOtherUserListModalLoading = true;
          this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.NON_AGREEMENT;
          this.optionTargetOtherUserItems = this.otherUserNonReplyItems;
          this.isOptionTargetOtherUserListModalLoading = false;
          this.showOptionTargetOtherUserListModal = true;

          break;
        }
      }
    }

    return;
  }

  /**
   * ### 確認済み一覧ボタン押下イベント
   */
  public onConfirmList(sendDocument: SendDocumentGetModel): void {
    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        this.isOptionTargetFamilyListModalLoading = true;
        this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.CONFIRM;
        this.optionTargetFamilyItems = this.familyReplyItems;
        this.isOptionTargetFamilyListModalLoading = false;
        this.showOptionTargetFamilyListModal = true;

        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        this.isOptionTargetOtherUserListModalLoading = true;
        this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.CONFIRM;
        this.optionTargetOtherUserItems = this.otherUserReplyItems;
        this.isOptionTargetOtherUserListModalLoading = false;
        this.showOptionTargetOtherUserListModal = true;

        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          this.isOptionTargetFamilyListModalLoading = true;
          this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.CONFIRM;
          this.optionTargetFamilyItems = this.familyReplyItems;
          this.isOptionTargetFamilyListModalLoading = false;
          this.showOptionTargetFamilyListModal = true;

          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          this.isOptionTargetOtherUserListModalLoading = true;
          this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.CONFIRM;
          this.optionTargetOtherUserItems = this.otherUserReplyItems;
          this.isOptionTargetOtherUserListModalLoading = false;
          this.showOptionTargetOtherUserListModal = true;

          break;
        }
      }
    }

    return;
  }

  /**
   * ### 未確認一覧ボタン押下イベント
   */
  public onNonConfirmList(sendDocument: SendDocumentGetModel): void {
    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        this.isOptionTargetFamilyListModalLoading = true;
        this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.NON_CONFIRM;
        this.optionTargetFamilyItems = this.familyNonReplyItems;
        this.isOptionTargetFamilyListModalLoading = false;
        this.showOptionTargetFamilyListModal = true;

        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        this.isOptionTargetOtherUserListModalLoading = true;
        this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.NON_CONFIRM;
        this.optionTargetOtherUserItems = this.otherUserNonReplyItems;
        this.isOptionTargetOtherUserListModalLoading = false;
        this.showOptionTargetOtherUserListModal = true;

        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          this.isOptionTargetFamilyListModalLoading = true;
          this.optionTargetFamilyListModalType = OptionTargetFamilyListModalType.NON_CONFIRM;
          this.optionTargetFamilyItems = this.familyNonReplyItems;
          this.isOptionTargetFamilyListModalLoading = false;
          this.showOptionTargetFamilyListModal = true;

          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          this.isOptionTargetOtherUserListModalLoading = true;
          this.optionTargetOtherUserListModalType = OptionTargetOtherUserListModalType.NON_CONFIRM;
          this.optionTargetOtherUserItems = this.otherUserNonReplyItems;
          this.isOptionTargetOtherUserListModalLoading = false;
          this.showOptionTargetOtherUserListModal = true;

          break;
        }
      }
    }

    return;
  }

  /**
   * ### 再送ボタン押下イベント
   *
   * - 再送ボタンが非活性の場合は即終了する
   * - 再送内容入力モーダルを表示する
   */
  public onRemind(sendDocument: SendDocumentGetModel): void {
    if (this._disableRemind == true) {
      return;
    }

    const sendDocumentRemindModel: SendDocumentRemindModel = {
      sendDocument: sendDocument,
      familyNonReplyItems: this.familyNonReplyItems,
      otherUserNonReplyItems: this.otherUserNonReplyItems
    };
    this.$emit("onRemind", sendDocumentRemindModel);

    return;
  }

  /** ### 送付日時を返す */
  get _sendDatetime(): string {
    if (this.sendDocument == null) {
      return "";
    }
    const sendDatetime: string = this.$dateUtil.convertNumberToString(this.sendDocument.sendDatetime as number, "yyyy年mm月dd日 hh:MM");
    return sendDatetime;
  }

  /** ### 宛先種別文字列を返す */
  get _typeLabel(): string {
    if (this.sendDocument == null) {
      return "";
    }

    switch (this.sendDocument.type) {
      case Type.ALL_FAMILY: {
        return "家族全員";
      }
      case Type.SELECTED_FAMILY: {
        return "家族個別";
      }
      case Type.SELECTED_OTHER_USER: {
        return "施設関係者個別";
      }
      case Type.SEND_MESSAGE_GROUP: {
        return "宛先グループ";
      }
      case Type.RESIDENT_GROUP: {
        return "利用者グループ";
      }
      case Type.OTHER_USER_GROUP: {
        return "施設関係者グループ";
      }
      default: {
        return "";
      }
    }
  }

  /** ### 宛先文字列を返す */
  get _sendTargetLabel(): string {
    if (this.sendDocument == null) {
      return "";
    }

    let sendTargets: string[] = [];
    switch (this.sendDocument.type) {
      case Type.ALL_FAMILY: {
        sendTargets.push("家族全員");
        break;
      }
      case Type.SELECTED_FAMILY: {
        for (const receiver of this.sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
          sendTargets.push(`${receiver.familyName}（${receiver.room}・${receiver.name}）`);
        }
        break;
      }
      case Type.SELECTED_OTHER_USER: {
        for (const receiver of this.sendDocument.receivers as Array<SendDocumentOtherUserReceiver>) {
          sendTargets.push(receiver.memberName);
        }
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        for (const group of this.sendDocument.sendMessageGroupInfos as Array<SendMessageGroupInfo>) {
          sendTargets.push(group.name);
        }
        break;
      }
      case Type.RESIDENT_GROUP: {
        for (const group of this.sendDocument.residentGroupInfos as Array<ResidentGroupInfo>) {
          sendTargets.push(group.name);
        }
        break;
      }
      case Type.OTHER_USER_GROUP: {
        for (const group of this.sendDocument.otherUserGroupInfos as Array<GroupOtherUserGroupInfo>) {
          sendTargets.push(group.name);
        }
        break;
      }
    }

    return sendTargets.join("、");
  }

  /** ### 文書の配信単位文字列を返す */
  get _unitLabel(): string {
    if (this.sendDocument == null) {
      return "";
    }

    switch (this.sendDocument.unit) {
      case Unit.SAME: {
        return "宛先全員同じ文書";
      }
      case Unit.SEPARATE: {
        return "利用者ごとに異なる文書";
      }
      default: {
        return "";
      }
    }
  }

  /** ### 文書の配信単位を返す */
  get _unit(): Unit {
    if (this.sendDocument == null) {
      return Unit.UNKNOWN;
    }

    return this.sendDocument.unit;
  }

  /** ### 添付文書名のリストを返す */
  get _fileNames(): string[] {
    if (this.sendDocument == null) {
      return [];
    }

    if (this.sendDocument.unit != Unit.SAME) {
      return [];
    }

    let fileNames: string[] = [];
    for (const fileUrl of this.sendDocument.fileUrls) {
      for (const file of fileUrl.files) {
        fileNames.push(file.name);
      }
    }

    return fileNames;
  }

  /** ### カテゴリ文字列を返す */
  get _category(): string {
    if (this.sendDocument == null) {
      return "";
    }

    const categoryName: string | undefined = this.categoryMap.get(this.sendDocument.categoryId);
    return categoryName != undefined ? categoryName : "";
  }

  /** ### タイトル文字列を返す */
  get _title(): string {
    if (this.sendDocument == null) {
      return "";
    }

    return this.sendDocument.title;
  }

  /** ### 本文文字列を返す */
  get _text(): string {
    if (this.sendDocument == null) {
      return "";
    }

    return this.sendDocument.text;
  }

  /** ### 確認しましたボタンを付与するかを返す */
  get _needConfirm(): boolean {
    if (this.sendDocument == null) {
      return false;
    }
    return this.sendDocument.needConfirm;
  }

  /** ### 同意しましたボタンを付与するかを返す */
  get _needAgreement(): boolean {
    if (this.sendDocument == null) {
      return false;
    }
    return this.sendDocument.needAgreement;
  }

  /** ### 返信した人数を返す */
  get _replyCount(): number {
    if (this.sendDocument == null) {
      return 0;
    }

    let replyCount: number = 0;
    switch (this.sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        replyCount = this.familyReplyItems.length;
        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        replyCount = this.otherUserReplyItems.length;
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (this.sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${this.sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (this.sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          replyCount = this.familyReplyItems.length;
        } else if (this.sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          replyCount = this.otherUserReplyItems.length;
        }
        break;
      }
    }

    return replyCount;
  }

  /** ### 返信していない人数を返す */
  get _nonreplyCount(): number {
    if (this.sendDocument == null) {
      return 0;
    }

    let replyCount: number = 0;
    switch (this.sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        replyCount = this.familyNonReplyItems.length;
        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        replyCount = this.otherUserNonReplyItems.length;
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (this.sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${this.sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (this.sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          replyCount = this.familyNonReplyItems.length;
        } else if (this.sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          replyCount = this.otherUserNonReplyItems.length;
        }
        break;
      }
    }

    return replyCount;
  }

  /** ### 再送ボタンの活性/非活性を返す */
  get _disableRemind(): boolean {
    if (this.familyNonReplyItems.length < 1 && this.otherUserNonReplyItems.length < 1) {
      return true;
    } else {
      return false;
    }
  }
}

export interface ResidentSeparateFiles {
  sortKey: string;
  files: Array<File>;
}
</script>

<style lang="scss" scoped>
#send-document-detail-tab {
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  // チェックボックスは横一列に並べる
  .checkboxs {
    .register-row {
      width: 180px;
    }
  }

  .file-name {
    padding: 6px 12px;
  }
  .preview-btn {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
  }
}
</style>
